import React, { useState, useEffect } from 'react';
import {
  Upload,
  Image,
  FileText,
  Folder,
  Grid,
  List,
  Search,
  Filter,
  RefreshCw,
  Plus,
  Settings
} from 'lucide-react';
import FileUpload from './FileUpload';
import MediaGallery from './MediaGallery';
import ImageCropper from './ImageCropper';
import api from '../../services/api';

interface MediaFile {
  id?: string;
  url: string;
  filename: string;
  size: number;
  mimetype: string;
  type?: string;
  uploadedAt?: string;
  uploadedBy?: string;
}

interface MediaManagerProps {
  uploadType?: 'image' | 'document' | 'profile';
  allowMultiple?: boolean;
  maxFiles?: number;
  maxSize?: number;
  onFilesChange?: (files: MediaFile[]) => void;
  initialFiles?: MediaFile[];
  showUpload?: boolean;
  showGallery?: boolean;
  showCropper?: boolean;
  className?: string;
}

type ViewMode = 'upload' | 'gallery' | 'cropper';
type LayoutMode = 'grid' | 'list';
type FilterType = 'all' | 'images' | 'documents';

const MediaManager: React.FC<MediaManagerProps> = ({
  uploadType = 'image',
  allowMultiple = true,
  maxFiles = 10,
  maxSize = 5,
  onFilesChange,
  initialFiles = [],
  showUpload = true,
  showGallery = true,
  showCropper = false,
  className = ''
}) => {
  const [files, setFiles] = useState<MediaFile[]>(initialFiles);
  const [viewMode, setViewMode] = useState<ViewMode>('gallery');
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('grid');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null);

  // Update files when initialFiles changes
  useEffect(() => {
    setFiles(initialFiles);
  }, [initialFiles]);

  // Notify parent component when files change
  useEffect(() => {
    onFilesChange?.(files);
  }, [files, onFilesChange]);

  const handleUpload = (uploadedFiles: MediaFile[]) => {
    setFiles(prev => [...prev, ...uploadedFiles]);
    if (!allowMultiple && uploadedFiles.length > 0) {
      setViewMode('gallery');
    }
  };

  const handleDelete = async (fileToDelete: MediaFile) => {
    try {
      setIsLoading(true);
      
      // Extract filename from URL for API call
      const urlParts = fileToDelete.url.split('/');
      const filename = urlParts[urlParts.length - 1];
      const type = urlParts[urlParts.length - 2]; // images, documents, profiles

      await api.delete(`/upload/file/${type}/${filename}`);
      setFiles(prev => prev.filter(f => f.url !== fileToDelete.url));
    } catch (error: any) {
      console.error('Error deleting file:', error);
      alert(error?.response?.data?.error?.message || 'Failed to delete file. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCrop = (croppedImageUrl: string) => {
    // Convert cropped image to file and upload
    fetch(croppedImageUrl)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
        // Here you would typically upload the cropped file
        // For now, we'll just add it to the gallery
        const newFile: MediaFile = {
          url: croppedImageUrl,
          filename: 'cropped-image.jpg',
          size: blob.size,
          mimetype: 'image/jpeg',
          uploadedAt: new Date().toISOString()
        };
        setFiles(prev => [...prev, newFile]);
        setViewMode('gallery');
      });
  };

  const filteredFiles = files.filter(file => {
    // Apply type filter
    if (filterType === 'images' && !file.mimetype.startsWith('image/')) {
      return false;
    }
    if (filterType === 'documents' && file.mimetype.startsWith('image/')) {
      return false;
    }

    // Apply search filter
    if (searchQuery && !file.filename.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    return true;
  });

  const getFileTypeCount = (type: FilterType) => {
    if (type === 'all') return files.length;
    if (type === 'images') return files.filter(f => f.mimetype.startsWith('image/')).length;
    if (type === 'documents') return files.filter(f => !f.mimetype.startsWith('image/')).length;
    return 0;
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-semibold text-gray-900">Media Manager</h2>
          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
            {showUpload && (
              <button
                onClick={() => setViewMode('upload')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === 'upload'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Upload className="w-4 h-4 inline mr-1" />
                Upload
              </button>
            )}
            {showGallery && (
              <button
                onClick={() => setViewMode('gallery')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === 'gallery'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Folder className="w-4 h-4 inline mr-1" />
                Gallery ({files.length})
              </button>
            )}
            {showCropper && (
              <button
                onClick={() => setViewMode('cropper')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === 'cropper'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Settings className="w-4 h-4 inline mr-1" />
                Crop
              </button>
            )}
          </div>
        </div>

        {/* Gallery Controls */}
        {viewMode === 'gallery' && (
          <div className="flex items-center space-x-2">
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Layout Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setLayoutMode('grid')}
                className={`p-2 rounded transition-colors ${
                  layoutMode === 'grid'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Grid View"
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setLayoutMode('list')}
                className={`p-2 rounded transition-colors ${
                  layoutMode === 'list'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Refresh */}
            <button
              onClick={() => window.location.reload()}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      {viewMode === 'gallery' && files.length > 0 && (
        <div className="flex items-center space-x-4 mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-1">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Filter:</span>
          </div>
          <div className="flex items-center space-x-2">
            {(['all', 'images', 'documents'] as FilterType[]).map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  filterType === type
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {type === 'all' && <span>All ({getFileTypeCount(type)})</span>}
                {type === 'images' && (
                  <span className="flex items-center space-x-1">
                    <Image className="w-3 h-3" />
                    <span>Images ({getFileTypeCount(type)})</span>
                  </span>
                )}
                {type === 'documents' && (
                  <span className="flex items-center space-x-1">
                    <FileText className="w-3 h-3" />
                    <span>Documents ({getFileTypeCount(type)})</span>
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="min-h-[400px]">
        {viewMode === 'upload' && (
          <FileUpload
            accept={uploadType === 'image' ? 'image/*' : uploadType === 'document' ? '.pdf,.doc,.docx,image/*' : 'image/*'}
            multiple={allowMultiple}
            maxFiles={maxFiles}
            maxSize={maxSize}
            uploadType={uploadType}
            onUpload={handleUpload}
            onError={(error) => alert(error)}
            showPreview={true}
          />
        )}

        {viewMode === 'gallery' && (
          <MediaGallery
            files={filteredFiles}
            onDelete={handleDelete}
            showActions={true}
            showDetails={true}
            layout={layoutMode}
            maxHeight="500px"
          />
        )}

        {viewMode === 'cropper' && (
          <ImageCropper
            onCrop={handleCrop}
            onCancel={() => setViewMode('gallery')}
            aspectRatio={uploadType === 'profile' ? 1 : undefined}
            maxWidth={uploadType === 'profile' ? 400 : 800}
            maxHeight={uploadType === 'profile' ? 400 : 600}
          />
        )}
      </div>

      {/* Quick Actions */}
      {viewMode === 'gallery' && showUpload && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setViewMode('upload')}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add More Files</span>
          </button>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
            <span>Processing...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaManager;